<template>
    <v-container fluid>
        <v-row>
            <v-col class="pl-5">
                <v-btn
                    v-if="initialLoad && mode !== 'read' && invoice.id != 0"
                    class="ml-2 mr-2"
                    color="primary"
                    @click="saveClick()"
                >
                    <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.save') }}
                </v-btn>
                <v-btn v-if="initialLoad && mode !== 'read'" class="ml-2 mr-2" color="primary" @click="saveClick(null)">
                    <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.saveAndExit') }}
                </v-btn>
                <v-btn v-if="initialLoad && mode === 'new'" class="ml-2 mr-2" color="success" @click="previewClick()">
                    <v-icon small>fa-download</v-icon>&nbsp;{{ $t('buttons.preview') }}
                </v-btn>
                <v-btn
                    v-if="
                        initialLoad &&
                        mode === 'read' &&
                        !invoice.cancelled &&
                        invoice.cancels === null &&
                        !invoice.gdpr_edit_lock
                    "
                    class="ml-2 mr-2"
                    color="primary"
                    @click="editClick()"
                >
                    <v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
                </v-btn>
                <v-btn
                    v-if="
                        initialLoad &&
                        mode === 'read' &&
                        !invoice.cancelled &&
                        invoice.cancels === null &&
                        !invoice.gdpr_edit_lock
                    "
                    class="ml-2 mr-2"
                    color="error"
                    @click="voidClick()"
                >
                    <v-icon small>fa-times</v-icon>&nbsp;{{ $t('buttons.void') }}
                </v-btn>
                <v-btn
                    v-if="
                        initialLoad &&
                        mode === 'read' &&
                        !invoice.cancelled &&
                        invoice.cancels === null &&
                        !invoice.gdpr_edit_lock &&
                        deletable
                    "
                    class="ml-2 mr-2 white--text"
                    color="#8a0f0f"
                    @click="deleteClick()"
                >
                    <v-icon small>fas fa-trash</v-icon>&nbsp;{{ $t('buttons.delete') }}
                </v-btn>
                <v-btn class="ml-2 mr-2" @click="backClick">
                    <v-icon small>fa-reply</v-icon>&nbsp;{{ $t('buttons.back') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" lg="4" md="6">
                <Dropdown
                    :readonly="mode !== 'new'"
                    v-model="invoice.billing_list_id"
                    :ajax="true"
                    :data="'billing-lists-for-invoices'"
                    :errorBorder="!invoiceValidation.billing_list_id"
                    :label="$t('fields.billingList')"
                ></Dropdown>
            </v-col>
            <v-col cols="12" lg="4" md="6">
                <v-btn
                    v-if="initialLoad && mode === 'edit'"
                    class="ml-2 mr-2"
                    color="primary"
                    @click="updateBillingListData()"
                >
                    {{ $t('buttons.updateData') }}
                </v-btn>
            </v-col>
            <v-col cols="12" lg="4" md="6" class="text-right" v-if="invoice.cancels != null">
                <v-btn class="ml-2 mr-2" color="red" @click="openInvoice(invoice.cancels)">
                    {{ $t('buttons.openVoidedInvoice') }}
                </v-btn>
            </v-col>
            <v-col cols="12" lg="4" md="6" class="text-right" v-if="invoice.cancelled == true">
                <v-btn class="ml-2 mr-2" color="red" @click="openInvoice(canceledBy)">
                    {{ $t('buttons.openInvoiceVoid') }}
                </v-btn>
            </v-col>
        </v-row>
        <template v-if="billingListLoaded">
            <v-row>
                <v-col cols="12" lg="4" md="6">
                    <v-row>
                        <v-col cols="12">
                            <v-card flat outlined>
                                <v-card-text>
                                    <v-row dense wrap>
                                        <v-col cols="12" class="mb-2 text-h6">{{ $t('labels.totals') }}</v-col>
                                        <v-col cols="12" md="6">
                                            <Dropdown
                                                :readonly="mode === 'read'"
                                                v-model="invoice.currency_id"
                                                :ajax="true"
                                                :data="'currencies-with-exchange'"
                                                :errorBorder="!invoiceValidation.currency_id"
                                                :label="$t('fields.currency')"
                                            ></Dropdown>
                                        </v-col>
                                        <v-col cols="12" md="6">&nbsp;</v-col>
                                        <v-col cols="12" md="6">
                                            <CurrencyField
                                                :readonly="true"
                                                v-model="invoice.amount_without_tax"
                                                :label="$t('fields.netValue')"
                                            ></CurrencyField>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <CurrencyField
                                                :readonly="true"
                                                v-model="taxAmount"
                                                :label="$t('fields.vatValue')"
                                            ></CurrencyField>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <CurrencyField
                                                :readonly="true"
                                                v-model="invoice.amount_with_tax"
                                                :label="$t('fields.totalValue')"
                                            ></CurrencyField>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <CurrencyField
                                                :readonly="true"
                                                v-model="eurAmount"
                                                :label="$t('fields.totalValueInEur')"
                                            ></CurrencyField>
                                        </v-col>
                                        <v-col cols="12">
                                            <TextArea
                                                :readonly="mode === 'read'"
                                                :label="$t('fields.poNumbers')"
                                                v-model="invoice.po_numbers"
                                            >
                                            </TextArea>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12">
                            <v-card flat outlined>
                                <v-card-text>
                                    <v-row dense wrap>
                                        <v-col cols="12" class="mb-2 text-h6">{{ $t('labels.provider') }}</v-col>
                                        <v-col cols="12" md="6">
                                            <Dropdown
                                                :readonly="mode !== 'new'"
                                                v-model="invoice.internal_office_id"
                                                :ajax="true"
                                                :data="'internal-offices'"
                                                :errorBorder="!invoiceValidation.internal_office_id"
                                                :label="'* ' + $t('fields.office')"
                                            ></Dropdown>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <Dropdown
                                                :readonly="mode !== 'new'"
                                                v-if="showOfficeRelatedData"
                                                v-model="invoice.internal_office_bank_account_id"
                                                :ajax="true"
                                                :data="'internal-office-bank-accounts/' + invoice.internal_office_id"
                                                :errorBorder="!invoiceValidation.internal_office_bank_account_id"
                                                :label="'* ' + $t('fields.account')"
                                            ></Dropdown>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <Dropdown
                                                :readonly="mode !== 'new'"
                                                v-model="invoice.invoice_sequence_id"
                                                :ajax="true"
                                                :data="'invoice-sequences'"
                                                :errorBorder="!invoiceValidation.invoice_sequence_id"
                                                :label="'* ' + $t('fields.sequence')"
                                            ></Dropdown>
                                        </v-col>
                                        <v-col cols="12" md="6" v-if="invoice.id != 0">
                                            <InputField
                                                :readonly="true"
                                                :label="$t('fields.invoiceNumber')"
                                                v-model="invoice.number"
                                            ></InputField>
                                        </v-col>
                                        <v-col cols="12">
                                            <TextArea
                                                :readonly="mode !== 'new'"
                                                :maxlength="500"
                                                :label="$t('fields.officeFooter')"
                                                v-model="invoice.office_footer"
                                            >
                                            </TextArea>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" lg="4" md="6">
                    <v-row>
                        <v-col cols="12">
                            <v-card flat outlined>
                                <v-card-text>
                                    <v-row dense wrap>
                                        <v-col cols="12" class="mb-2 text-h6">{{ $t('labels.customer') }}</v-col>
                                        <v-col cols="12">
                                            <Dropdown
                                                :readonly="mode === 'read'"
                                                v-model="invoice.entity_id"
                                                :ajax="true"
                                                :data="'entities'"
                                                :errorBorder="!invoiceValidation.entity_id"
                                                :label="'* ' + $t('fields.entity')"
                                            ></Dropdown>
                                        </v-col>
                                        <v-col cols="6" v-if="invoice.entity_id !== null">
                                            {{ invoice.entity_data.address }}<br />
                                            {{ invoice.entity_data.post_code }}<br />
                                            {{ invoice.entity_data.town
                                            }}<br /><!-- TODO blists add city after adding city to entities -->
                                            <template v-if="invoice.entity_data.country !== undefined"
                                                >{{ invoice.entity_data.country.name_en }}<br
                                            /></template>
                                            VAT ID: {{ invoice.entity_data.vat_id }}
                                        </v-col>
                                        <v-col cols="6" v-if="invoice.entity_id !== null">
                                            <v-btn class="float-right" color="primary" @click="updateEntityData()">
                                                {{ $t('buttons.updateData') }}
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="12">
                                            <TextArea
                                                :readonly="mode === 'read'"
                                                :maxlength="500"
                                                :label="$t('fields.invoiceNotes')"
                                                v-model="invoice.entity_header"
                                            >
                                            </TextArea>
                                        </v-col>
                                        <v-col cols="12">
                                            <TextArea
                                                :readonly="mode === 'read'"
                                                :maxlength="500"
                                                :label="$t('fields.vatExemptionComment')"
                                                v-model="invoice.entity_footer"
                                            >
                                            </TextArea>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" lg="4" md="6">
                    <v-row>
                        <v-col cols="12">
                            <v-card flat outlined>
                                <v-card-text>
                                    <v-row dense wrap>
                                        <v-col cols="12" class="mb-2 text-h6">{{ $t('labels.general') }}</v-col>
                                        <v-col cols="12" class="pb-4">
                                            <DateTimePicker
                                                :readonly="mode !== 'new'"
                                                v-model="invoice.time_of_issue"
                                                :errorBorder="!invoiceValidation.time_of_issue"
                                                :dateLabel="'* ' + $t('fields.dateOfIssue')"
                                                :timeLabel="'* ' + $t('fields.timeOfIssue')"
                                            ></DateTimePicker>
                                        </v-col>
                                        <v-col cols="12">
                                            <DatePicker
                                                :readonly="mode === 'read'"
                                                v-model="invoice.delivery_date"
                                                :errorBorder="!invoiceValidation.delivery_date"
                                                :dateLabel="'* ' + $t('fields.deliveryDate')"
                                            ></DatePicker>
                                        </v-col>
                                        <v-col cols="12">
                                            <DatePicker
                                                :readonly="mode === 'read'"
                                                v-model="invoice.due_date"
                                                :errorBorder="!invoiceValidation.due_date"
                                                :dateLabel="'* ' + $t('fields.dueDate')"
                                            ></DatePicker>
                                        </v-col>
                                        <v-col cols="12">
                                            <InputField
                                                :readonly="mode === 'read'"
                                                :label="$t('fields.placeOfIssue')"
                                                v-model="invoice.place_of_issue"
                                            ></InputField>
                                        </v-col>
                                        <v-col cols="12">
                                            <Dropdown
                                                :readonly="mode !== 'new'"
                                                v-model="invoice.payment_method_id"
                                                :ajax="true"
                                                :data="'payment-methods'"
                                                :errorBorder="!invoiceValidation.payment_method_id"
                                                :label="'* ' + $t('fields.paymentMethod')"
                                            ></Dropdown>
                                        </v-col>
                                        <v-col cols="12">
                                            <Dropdown
                                                :readonly="mode === 'read'"
                                                v-model="invoice.user_id"
                                                :ajax="true"
                                                :data="'users'"
                                                :errorBorder="!invoiceValidation.user_id"
                                                :label="'* ' + $t('fields.operator')"
                                            ></Dropdown>
                                        </v-col>
                                        <v-col cols="12">
                                            <CheckBox
                                                :readonly="mode === 'read'"
                                                :label="$t('fields.vatApplicable')"
                                                v-model="invoice.vat"
                                            ></CheckBox>
                                        </v-col>
                                        <v-col cols="12" class="mt-2"> Billing period: </v-col>
                                        <v-col cols="12" md="6">
                                            <DatePicker
                                                :readonly="mode === 'read'"
                                                v-model="invoice.billing_period_from"
                                                :errorBorder="!invoiceValidation.billing_period_from"
                                                :dateLabel="'* ' + $t('fields.from')"
                                            ></DatePicker>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <DatePicker
                                                :readonly="mode === 'read'"
                                                v-model="invoice.billing_period_to"
                                                :errorBorder="!invoiceValidation.billing_period_to"
                                                :dateLabel="'* ' + $t('fields.to')"
                                            ></DatePicker>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </template>

        <v-dialog persistent v-model="currencyDialog" width="400">
            <v-card>
                <v-card-title style="word-break: normal">{{ $t('labels.selectInvoiceCurrency') }}</v-card-title>
                <v-card-text>
                    <v-btn
                        v-for="c in currencyChoices"
                        :key="c.id"
                        block
                        class="mb-2"
                        color="primary"
                        @click="selectInvoiceCurrency(c.id)"
                    >
                        {{ c.name }}
                    </v-btn>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import InputField from './trinkets/InputField';
import CheckBox from './trinkets/CheckBox';
import Dropdown from './trinkets/Dropdown';
import Invoice from '../models/Invoice';
import DatePicker from '@/components/trinkets/DatePicker';
import DateTimePicker from '@/components/trinkets/DateTimePicker';
import CurrencyField from '@/components/trinkets/CurrencyField';
import TextArea from './trinkets/TextArea.vue';

export default {
    name: 'Invoice',
    components: {
        TextArea,
        CurrencyField,
        DateTimePicker,
        DatePicker,
        CheckBox,
        InputField,
        Dropdown,
    },
    data: function () {
        return {
            invoice: new Invoice({id: this.$route.params.id}),
            invoiceValidation: new Invoice({}, true),
            initialLoad: false,
            mode: 'new',
            lockKey: 'invoice',
            lockTimer: null,
            taxAmount: 0,
            eurAmount: 0,
            billingListLoaded: false,
            totalsRequestId: 0,
            showOfficeRelatedData: true,
            canceledBy: null,
            dueDays: 0,
            deletable: false,
            currencyDialog: false,
            currencyChoices: [],
        };
    },
    watch: {
        'invoice.time_of_issue': {
            handler: function () {
                if (this.initialLoad) {
                    this.calculateTotals();
                    let moment = require('moment');
                    this.invoice.due_date = moment(this.invoice.time_of_issue)
                        .add(this.dueDays, 'day')
                        .format('YYYY-MM-DD');
                }
            },
            deep: true,
        },
        'invoice.billing_list_id': {
            handler: function () {
                if (!this.initialLoad) return;
                this.loadBillingListData();
            },
            deep: true,
        },
        'invoice.currency_id': {
            handler: function () {
                if (!this.initialLoad) return;
                this.calculateTotals();
            },
            deep: true,
        },
        'invoice.internal_office_id': {
            handler: function () {
                if (!this.initialLoad) return;
                let vm = this;
                this.$APICall('get', 'internal-offices/' + this.invoice.internal_office_id, {}, (data, success) => {
                    if (success) {
                        vm.invoice.office_data = JSON.parse(JSON.stringify(data));
                        vm.invoice.office_footer = data.office_footer;
                        vm.showOfficeRelatedData = false;
                        vm.invoice.internal_office_bank_account_id = null;
                        vm.invoice.invoice_sequence_id = vm.invoice.internal_office_id == 2 ? 2 : 1;
                        vm.$nextTick(() => {
                            vm.showOfficeRelatedData = true;
                        });
                    }
                });
            },
            deep: true,
        },
        'invoice.entity_id': {
            handler: function () {
                if (!this.initialLoad) return;
                let vm = this;
                this.$APICall('get', 'entities/' + this.invoice.entity_id, {}, (data, success) => {
                    if (success) {
                        vm.invoice.entity_data = JSON.parse(JSON.stringify(data));
                        vm.invoice.entity_header = data.invoice_notes;
                        vm.invoice.entity_footer = data.vat_exemption_comment;
                        vm.dueDays = data.due_days;
                        let moment = require('moment');
                        vm.invoice.due_date = moment().add(data.due_days, 'day').format('YYYY-MM-DD');
                    }
                });
            },
            deep: true,
        },
        'invoice.vat': {
            handler: function () {
                if (!this.initialLoad) return;
                this.calculateTotals();
            },
            deep: true,
        },
    },
    methods: {
        loadBillingListData() {
            let vm = this;
            vm.billingListLoaded = false;
            if (Number.isInteger(this.invoice.billing_list_id)) {
                this.$APICall(
                    'get',
                    'billing-list/' + this.invoice.billing_list_id + '/invoice-data',
                    {},
                    (data, success) => {
                        if (success) {
                            vm.invoice.vat = data.vat_applicable;
                            vm.invoice.entity_id = data.entity_id;
                            // Seed invoice currency from the billing list. One currency → use it;
                            // several → seed the first and let the user pick via dialog; none → EUR.
                            const choices = data.list_currencies || [];
                            if (choices.length === 1) {
                                vm.invoice.currency_id = choices[0].id;
                            } else if (choices.length > 1) {
                                vm.invoice.currency_id = choices[0].id;
                                vm.currencyChoices = choices;
                                vm.currencyDialog = true;
                            } else {
                                vm.invoice.currency_id = 1;
                            }
                            vm.invoice.entity_header = data.entity_header;
                            vm.invoice.entity_footer = data.entity_footer;
                            vm.invoice.po_numbers = data.po_numbers;
                            vm.invoice.billing_period_from = data.billing_period_from;
                            vm.invoice.billing_period_to = data.billing_period_to;

                            vm.invoice.items = [];
                            let itemIndex = 0;
                            for (let item of data.items) {
                                let newItem = JSON.parse(JSON.stringify(item));
                                newItem.item_index = ++itemIndex;
                                newItem.invoice_id = null;
                                vm.invoice.items.push(newItem);
                            }

                            vm.$nextTick(() => {
                                vm.calculateTotals();
                                vm.billingListLoaded = true;
                            });
                        }
                    },
                );
            }
        },
        openInvoice(id) {
            let routeData = this.$router.resolve('/invoices/' + id);
            window.open(routeData.href, '_blank');
        },
        updateBillingListData() {
            this.loadBillingListData();
        },
        selectInvoiceCurrency(id) {
            this.invoice.currency_id = id;
            this.currencyDialog = false;
            this.calculateTotals();
        },
        updateEntityData() {
            let vm = this;
            this.$APICall('get', 'entities/' + this.invoice.entity_id, {}, (data, success) => {
                if (success) {
                    vm.invoice.entity_data = JSON.parse(JSON.stringify(data));
                }
            });
        },
        calculateTotals() {
            // Totals come from the backend (Invoice::resolveTotals) — the same calculation used when saving
            // and for the PDF, so the form, the invoice list and the PDF always show the same amounts.
            let vm = this;
            let requestId = ++this.totalsRequestId;
            this.$APICall(
                'post',
                'invoices/totals',
                this.invoice,
                (data, success) => {
                    // ignore responses overtaken by a newer request
                    if (requestId !== vm.totalsRequestId || !success) return;
                    if (!data.success) {
                        vm.$alert(vm.$t('alerts.' + data.alert_key));
                        return;
                    }
                    vm.invoice.amount_without_tax = data.net;
                    vm.invoice.amount_with_tax = data.gross;
                    vm.taxAmount = data.vat;
                    vm.eurAmount = data.eur;
                },
                false,
            );
        },
        editClick() {
            let vm = this;
            this.$setLock(this, this.lockKey + '|' + this.invoice.id, (data) => {
                if (data.success) {
                    vm.mode = 'edit';
                    vm.lockTimer = setInterval(() => {
                        vm.$setLock(vm, vm.lockKey + '|' + vm.invoice.id);
                    }, 10000);
                } else {
                    vm.$alert(
                        this.$t('alerts.recordLocked')
                            .split('%name%')
                            .join(data.locked_by.first_name + ' ' + data.locked_by.last_name),
                    );
                }
            });
        },
        removeLock() {
            this.$APICall('delete', 'lock', {key: this.lockKey + '|' + this.invoice.id}, () => {});
        },
        backClick() {
            this.$router.go(-1);
        },
        saveClick(after = null) {
            let success = true;
            let message = '';

            if (this.invoice.internal_office_id == null) {
                success = false;
                this.invoiceValidation.internal_office_id = false;
            } else {
                this.invoiceValidation.internal_office_id = true;
            }

            if (this.invoice.internal_office_bank_account_id == null) {
                success = false;
                this.invoiceValidation.internal_office_bank_account_id = false;
            } else {
                this.invoiceValidation.internal_office_bank_account_id = true;
            }

            if (this.invoice.invoice_sequence_id == null) {
                success = false;
                this.invoiceValidation.invoice_sequence_id = false;
            } else {
                this.invoiceValidation.invoice_sequence_id = true;
            }

            if (this.invoice.delivery_date == null) {
                success = false;
                this.invoiceValidation.delivery_date = false;
            } else {
                this.invoiceValidation.delivery_date = true;
            }

            if (this.invoice.due_date == null) {
                success = false;
                this.invoiceValidation.due_date = false;
            } else {
                this.invoiceValidation.due_date = true;
                if (
                    this.invoice.time_of_issue !== null &&
                    this.invoice.due_date + ' 23:59:59' < this.invoice.time_of_issue
                ) {
                    success = false;
                    message += this.$t('alerts.invoiceDueDateError');
                    this.invoiceValidation.due_date = false;
                }
            }

            if (this.invoice.time_of_issue == null) {
                success = false;
                this.invoiceValidation.time_of_issue = false;
            } else {
                this.invoiceValidation.time_of_issue = true;
            }

            if (this.invoice.payment_method_id == null) {
                success = false;
                this.invoiceValidation.payment_method_id = false;
            } else {
                this.invoiceValidation.payment_method_id = true;
            }

            if (this.invoice.user_id == null) {
                success = false;
                this.invoiceValidation.user_id = false;
            } else {
                this.invoiceValidation.user_id = true;
            }

            if (this.invoice.billing_period_from == null) {
                success = false;
                this.invoiceValidation.billing_period_from = false;
            } else {
                this.invoiceValidation.billing_period_from = true;
            }

            if (this.invoice.billing_period_to == null) {
                success = false;
                this.invoiceValidation.billing_period_to = false;
            } else {
                this.invoiceValidation.billing_period_to = true;
            }

            if (success) {
                let vm = this;
                let action = 'post';
                let addition = '';
                if (vm.mode === 'edit') {
                    action = 'put';
                    addition = '/' + vm.invoice.id;
                }
                this.$APICall(action, 'invoices' + addition, this.invoice, (data, success) => {
                    if (success) {
                        if (data.success) {
                            if (vm.mode === 'new') {
                                vm.$notify(vm.$t('alerts.basicAdd'));
                            }
                            if (vm.mode === 'edit') {
                                vm.$notify(vm.$t('alerts.basicUpdate'));
                            }
                            vm.mode = 'read';

                            vm.$triggerReload();

                            if (after !== null) {
                                after();
                            } else {
                                let routeData = this.$router.resolve('/invoices');
                                window.open(routeData.href, '_self');
                            }
                        } else {
                            vm.$alert(vm.$t('alerts.' + data.alert_key));
                        }
                    }
                });
            } else {
                if (message !== '') {
                    this.$alert(message);
                }
            }
        },
        voidClick() {
            let vm = this;
            this.$verify(this.$t('alerts.voidQuestion'), (response) => {
                if (response) {
                    vm.$APICall('post', 'invoices/void', {invoice_id: vm.invoice.id}, (data, success) => {
                        if (success && data.success === false) {
                            vm.$alert(vm.$t('alerts.' + data.alert_key));
                            return;
                        }
                        vm.backClick();
                    });
                }
            });
        },
        deleteClick() {
            let vm = this;
            this.$verify(this.$t('alerts.deleteInvoiceQuestion'), (response) => {
                if (response) {
                    vm.$APICall('post', 'invoices/delete', {invoice_id: vm.invoice.id}, (data, success) => {
                        if (success && !data.message) {
                            vm.$notify(vm.$t('alerts.invoiceDeleted'));
                            vm.backClick();
                        } else {
                            if (data.message) {
                                vm.$alert(vm.$t('alerts.invoiceDeleteInvalid'));
                            }
                        }
                    });
                }
            });
        },
        previewClick() {
            let success = true;

            if (this.invoice.internal_office_id == null) {
                success = false;
                this.invoiceValidation.internal_office_id = false;
            } else {
                this.invoiceValidation.internal_office_id = true;
            }

            if (this.invoice.internal_office_bank_account_id == null) {
                success = false;
                this.invoiceValidation.internal_office_bank_account_id = false;
            } else {
                this.invoiceValidation.internal_office_bank_account_id = true;
            }

            if (this.invoice.invoice_sequence_id == null) {
                success = false;
                this.invoiceValidation.invoice_sequence_id = false;
            } else {
                this.invoiceValidation.invoice_sequence_id = true;
            }

            if (this.invoice.delivery_date == null) {
                success = false;
                this.invoiceValidation.delivery_date = false;
            } else {
                this.invoiceValidation.delivery_date = true;
            }

            if (this.invoice.due_date == null) {
                success = false;
                this.invoiceValidation.due_date = false;
            } else {
                this.invoiceValidation.due_date = true;
                if (
                    this.invoice.time_of_issue !== null &&
                    this.invoice.due_date + ' 23:59:59' < this.invoice.time_of_issue
                ) {
                    success = false;
                    this.invoiceValidation.due_date = false;
                }
            }

            if (this.invoice.time_of_issue == null) {
                success = false;
                this.invoiceValidation.time_of_issue = false;
            } else {
                this.invoiceValidation.time_of_issue = true;
            }

            if (this.invoice.payment_method_id == null) {
                success = false;
                this.invoiceValidation.payment_method_id = false;
            } else {
                this.invoiceValidation.payment_method_id = true;
            }

            if (this.invoice.user_id == null) {
                success = false;
                this.invoiceValidation.user_id = false;
            } else {
                this.invoiceValidation.user_id = true;
            }

            if (this.invoice.billing_period_from == null) {
                success = false;
                this.invoiceValidation.billing_period_from = false;
            } else {
                this.invoiceValidation.billing_period_from = true;
            }

            if (this.invoice.billing_period_to == null) {
                success = false;
                this.invoiceValidation.billing_period_to = false;
            } else {
                this.invoiceValidation.billing_period_to = true;
            }

            if (success) {
                let vm = this;
                this.$APICall(
                    'post',
                    'invoices/pdf/preview',
                    this.invoice,
                    (data, success) => {
                        if (success) {
                            window.open(data.link);
                        } else {
                            vm.$alert(vm.$t('alerts.basicError'));
                        }
                    },
                    true,
                );
            }
        },
    },
    beforeRouteLeave(to, from, next) {
        if (this.mode === 'edit') {
            let vm = this;
            this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
                if (result) {
                    if (vm.lockTimer !== null) clearInterval(this.lockTimer);
                    vm.removeLock();
                    next(true);
                } else {
                    next(false);
                }
            });
        } else {
            next(true);
        }
    },
    mounted() {
        if (!this.$myStore.state.loggedIn) {
            this.$router.replace('login');
        }
        let vm = this;
        if (this.invoice.id != 0) {
            this.mode = 'read';
            this.$APICall('get', 'invoices/' + this.invoice.id, {}, (data, success) => {
                if (success) {
                    let usableData = JSON.parse(JSON.stringify(data));
                    vm.$myStore.commit('setBreadcrumbs', [
                        {
                            text: vm.$t('modules.invoices'),
                            href: '/#/invoices',
                            disabled: false,
                        },
                        {
                            text: vm.invoice.email,
                            href: '/#/invoices/' + vm.invoice.id,
                            disabled: true,
                        },
                    ]);
                    vm.$nextTick(() => {
                        vm.initialLoad = true;
                        vm.billingListLoaded = true;
                        vm.calculateTotals();
                    });
                    vm.invoice = usableData.invoice;
                    vm.canceledBy = usableData.canceled_by;
                    vm.deletable = usableData.invoice_deletable;
                    vm.$APICall('get', 'entities/' + vm.invoice.entity_id, {}, (data, success) => {
                        if (success) {
                            vm.dueDays = data.due_days;
                        }
                    });
                }
            });
        } else {
            this.$myStore.commit('setBreadcrumbs', [
                {
                    text: this.$t('modules.invoices'),
                    href: '/#/invoices',
                    disabled: false,
                },
                {
                    text: this.$t('modules.newInvoice'),
                    href: '/#/invoices/0',
                    disabled: true,
                },
            ]);
            this.mode = 'new';
            this.initialLoad = true;

            let moment = require('moment');
            this.invoice.time_of_issue = moment().format('YYYY-MM-DD HH:mm:ss');
            this.invoice.delivery_date = moment().format('YYYY-MM-DD');
            this.invoice.due_date = moment().format('YYYY-MM-DD');

            this.$nextTick(() => {
                if (Number.isInteger(parseInt(vm.$route.params.billing))) {
                    vm.invoice.billing_list_id = parseInt(vm.$route.params.billing);

                    vm.billingListLoaded = true;
                }
            });
        }
    },
};
</script>

<style scoped></style>
